import { Injectable, inject } from '@angular/core';

import { concatMap, map, mergeMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { CategoryWifiProfileConfigurationService, ModelNameFlags } from '@era/settings-sdk';
import { environment } from '@rocksoft/environment';
import { from } from 'rxjs';
import { safeMap } from '../effects.helpers';
import { getAncestors } from '../router/category';
import {
  CategoryWifiProfilePresetConfigurationApiActions,
  CategoryWifiProfilePresetConfigurationEffectActions,
  CategoryWifiProfilePresetConfigurationResolverActions,
  CategoryWifiProfilePresetConfigurationViewActions,
} from './category-wifi-profile-preset-configuration.actions';

@Injectable()
export class CategoryWifiProfileEffects {
  private readonly actions = inject(Actions);
  private readonly categoryWifiProfileService = inject(CategoryWifiProfileConfigurationService);

  protected features = environment.features;

  /**
   * The category wifi profile configuration has been updated.
   * Effect: we submit the update to the api and dispatch an error if applicable.
   */
  categoryWifiProfileLinkUpdatedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryWifiProfilePresetConfigurationViewActions.wifiProfilePresetConfigurationsUpdated),
      mergeMap((action) => {
        const categoryType = action.category.categoryType;

        const command = {
          wifiProfilePresets: action.updatedWifiProfilesLinkedToCategory.map((x) => ({
            wifiProfilePresetId: x.wifiProfileId,
            models: x.models,
          })),
        };

        return safeMap(
          this.categoryWifiProfileService.putWifiProfileToCategory({
            categoryType,
            categoryId: action.category.categoryId,
            setWifiProfilesForCategoryCommand: command,
          }),
          (updatedWifiProfiles) =>
            CategoryWifiProfilePresetConfigurationApiActions.categoryWifiProfilePresetConfigurationsUpdated({
              category: action.category,
              wifiProfiles: updatedWifiProfiles,
            }),
          (error) =>
            CategoryWifiProfilePresetConfigurationApiActions.categoryWifiProfilePresetConfigurationsUpdatedError({
              category: action.category,
              error,
            })
        );
      })
    );
  });

  /**
   * The resolver has requested wifi profiles for a category.
   * Effect: we dispatch actions for the category and all its ancestors.
   */
  categoryWifiProfilesResolverEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryWifiProfilePresetConfigurationResolverActions.categoryWifiProfilePresetConfigurationsRequested),
      concatMap((action) => {
        const ancestors = getAncestors(action.category);
        return from(
          ancestors.map((ancestor) =>
            CategoryWifiProfilePresetConfigurationEffectActions.categoryWifiProfilePresetConfigurationsRequested({
              category: ancestor,
              modelNames: action.modelNames,
            })
          )
        );
      })
    );
  });

  /**
   * The (re)load of the category wifi profiles has been requested.
   * Effect: we load the state from the api.
   */
  categoryWifiProfilesRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryWifiProfilePresetConfigurationEffectActions.categoryWifiProfilePresetConfigurationsRequested),
      mergeMap((action) => {
        const categoryType = action.category.categoryType;

        return safeMap(
          this.categoryWifiProfileService.getWifiProfilesForCategory({
            categoryType,
            categoryId: action.category.categoryId,
          }),
          (categoryWifiProfiles) =>
            CategoryWifiProfilePresetConfigurationApiActions.categoryWifiProfilePresetConfigurationsLoaded({
              wifiProfiles: categoryWifiProfiles,
            }),
          (error) =>
            CategoryWifiProfilePresetConfigurationApiActions.categoryWifiProfilePresetConfigurationsLoadedError({
              error,
            })
        );
      })
    );
  });

  /**
   * The wifi profiles couldn't be updated.
   * Effect: we request a reload of the state from the api.
   */
  categoryWifiProfilesUpdatedErrorEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryWifiProfilePresetConfigurationApiActions.categoryWifiProfilePresetConfigurationsUpdatedError),
      map((action) =>
        CategoryWifiProfilePresetConfigurationEffectActions.categoryWifiProfilePresetConfigurationsRequested({
          category: action.category,
          modelNames: [ModelNameFlags.All],
        })
      )
    );
  });
}
